
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import CancelIcon from '@mui/icons-material/Cancel';
import DraftIcon from '@mui/icons-material/EditNote';
import EditIcon from '@mui/icons-material/Edit';
import LaunchIcon from '@mui/icons-material/Launch';
import LaptopIcon from '@mui/icons-material/Laptop';
import MapIcon from '@mui/icons-material/Map';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Stack from '@mui/material/Stack';

import {formatAppointmentAddress} from '../../helpers/appointments';
import SessionFormV2 from '../sessions/SessionFormV2';
import SessionNoteForm from '../sessions/SessionNoteForm';
import CancelSessionDialog from '../sessions/CancelSessionDialog';

const getModes = ({
  session,
  onModeChange,
  navigate,
}) => [
  {
    value: 'details',
    label: 'View Details',
    Icon: LaunchIcon,
    onClick: e => navigate('/providers/sessions/view/' + session.id),
    maxWidth: 'xs',
    isDisabled: false,
  },
  {
    value: 'edit',
    label: 'Edit Session',
    Icon: EditIcon,
    onClick: e => onModeChange(e, 'edit'),
    maxWidth: 'md',
    isDisabled: session.is_canceled,
  },
  {
    value: 'notes',
    label: session.is_completed ? 'Addendum Note' : 'Edit Note',
    Icon: DraftIcon,
    onClick: e => onModeChange(e, 'notes'),
    maxWidth: 'lg',
    isDisabled: session.is_canceled,
  },
  {
    value: 'cancel',
    label: 'Cancel Session',
    Icon: CancelIcon,
    onClick: e => onModeChange(e, 'cancel'),
    maxWidth: 'xs',
    isDisabled: session.is_canceled || session.is_completed,
  },
  {
    value: 'location',
    label: (
      session.appointment_scheduled?.location_type === 'VIRTUAL'
      ? 'Open Virtual Meeting'
      : 'Open in Google Maps'
    ),
    Icon: (
      session.appointment_scheduled?.location_type === 'VIRTUAL'
      ? LaptopIcon
      : MapIcon
    ),
    href: (
      session.appointment_scheduled?.location_type === 'VIRTUAL'
      ? session.appointment_scheduled.session_meeting_url
      : `https://maps.google.com/?q=${formatAppointmentAddress(session.appointment_scheduled)}`
    ),
    maxWidth: 'xs',
    isDisabled: false,
  },
];

const ActionMenu = ({modes}) => (
  <Stack direction='row' justifyContent='center'>
    <MenuList>
      {modes.map(m => 
        <MenuItem 
          key={m.value}
          onClick={m.onClick} 
          color='primary'
          href={m.href}
          target={m.href ? '_blank' : undefined}
          disabled={m.isDisabled}
          component={m.href ? 'a' : undefined}
        >
          <ListItemIcon>
            <m.Icon fontSize="small"/>
          </ListItemIcon>
          <ListItemText>{m.label}</ListItemText>
        </MenuItem>
      )}
    </MenuList>
  </Stack>
);

const Content = ({
  session, 
  mode,
  modes,
  onClose,
  setMode,
}) => {
  switch (mode) {
    case 'edit':
      return (
        <SessionFormV2
          session={session}
          isPatient={false}
          isEditing={true}
          onClose={onClose}
        />
      );
    case 'cancel':
      return (
        <CancelSessionDialog 
          useDialog={false}
          session={session}
          isPatient={false}
          onClose={onClose}
          onReschedule={() => {
            setMode('schedule');
          }}
        />
      );
    case 'notes':
      return (
        <SessionNoteForm 
          session={session}
          onSubmitSuccess={onClose}
        />
      );
    case 'schedule':
      return (
        <SessionFormV2
          defaultPatientId={session?.patient?.id}
          isPatient={false}
          isEditing={false}
          onClose={onClose}
        />
      );
    default:
      return (
        <ActionMenu
          modes={modes}
        />
      );
    
  }
};

const SessionActions = ({
  session, 
  onClose = () => true,
}) => {
  const [mode, setMode] = useState('menu');
  const navigate = useNavigate();
  const modes = getModes({ 
    session,
    onClose,
    onModeChange: (e, mode) => setMode(mode), 
    navigate,
  });
  return (
    <Box 
      maxWidth={
        mode === 'menu' ? 'xs' :
        (
          mode.maxWidth 
          || 'md'
        )
      } 
      sx={{p: 2}}
    >
      <Stack>
        <Content 
          mode={mode}
          modes={modes}
          session={session}
          onClose={onClose}
          setMode={setMode}
        />
      </Stack>
    </Box>
  );
};

export default SessionActions;

